import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import {
  BETierSetFormat,
  ConditionTierType,
  RewardType,
  TierDetailsType,
  TierInTierSetType,
  TierPayload,
  TierSetType,
} from "types/tierSets";
import { TranslationType } from "types/translations";
import { getEnvVariable } from "utilities/env";

export const useTiers = () => {
  const { code: tenant } = useAppSelector(selectCurrentStoreData);
  const { fetcher, arrayFetcher } = useAuth();

  const useListOfTiers = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<TierDetailsType>> => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/tier${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteTiersList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<TierDetailsType>>(
      (index) =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/tier${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const addTier = async (payload: Omit<TierPayload, "photo">) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/create`,
      {
        body: JSON.stringify({ level: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const addTierSet = async (values: BETierSetFormat) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tierSet`,
      {
        body: JSON.stringify({ tierSet: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const setPhoto = async (tierId: string, file: any) => {
    const formData = new FormData();
    formData.append("photo[file]", file);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}/photo`,
      { body: formData, method: "post" },
    );
    if (response.status === 200) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const editTier = async (
    tierId: string,
    { ...payload }: Omit<TierPayload, "photo">,
  ) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}`,
      {
        body: JSON.stringify({ level: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editTierSet = async (tierSetId: string, values: BETierSetFormat) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tierSet/${tierSetId}`,
      {
        body: JSON.stringify({ tierSet: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );

    if (response.ok) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useTierData = (tierId: string | string[]) => {
    const isSingleRequest = typeof tierId === "string";

    return useSWR(
      () =>
        tenant && tierId !== ""
          ? isSingleRequest
            ? `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}`
            : tierId.map(
                (tier) =>
                  `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tier}`,
              )
          : null,
      isSingleRequest ? fetcher : arrayFetcher,
    );
  };

  const activateTier = async (tierId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}/activate`,
      {
        body: JSON.stringify({ active: true }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const deactivateTier = async (tierId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}/activate`,
      {
        body: JSON.stringify({ active: false }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const removeTier = async (tierId: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tier/${tierId}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useTierStatistics = () => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/analytics/levels`
          : null,
      fetcher,
    );
  };

  const useTierSetData = (tierSetId?: string) => {
    return useSWR<TierSetType>(
      () =>
        tierSetId &&
        tenant &&
        `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/tierSet/${tierSetId}`,
      fetcher,
    );
  };

  const useTierSetTiers = (tierSetId?: string, props?: ListingProps) => {
    return useSWR<{ items: TierInTierSetType[] }>(
      () =>
        tierSetId &&
        tenant &&
        `${getEnvVariable(
          "REACT_APP_API_URL",
        )}/${tenant}/tierSet/${tierSetId}/tiers${querystring(props)}`,
      fetcher,
    );
  };

  const updateTiersInTierSet = async ({
    tierSetId,
    tiers,
  }: {
    tiers: {
      levelId?: string;
      translations: TranslationType;
      conditions: ConditionTierType[];
      rewards: RewardType[];
      active: boolean;
    }[];
    tierSetId?: string;
  }) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/tierSet/${tierSetId}/tiers`,
      {
        body: JSON.stringify({ tiers: tiers }),
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      },
    );

    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useListOfTierSets = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<TierSetType>> => {
    return useSWR(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/tierSet${querystring(props)}`
          : null,
      fetcher,
    );
  };

  return {
    activateTier,
    addTier,
    addTierSet,
    deactivateTier,
    editTier,
    editTierSet,
    removeTier,
    setPhoto,
    useListOfTiers,
    useTierData,
    useTierSetData,
    useTierStatistics,
    useTierSetTiers,
    updateTiersInTierSet,
    useListOfTierSets,
    useInfiniteTiersList,
  };
};
